<template>
  <div class="billing-line-content__container">
    <div class="billing-line-content__block">
      <span class="sk-text-large-semibold">
        {{ $t('fill_billing_info.billing_line.billing_infos') }}
      </span>

      <GlobalEvents
        @keyup.escape="clearSuggestedCompanyRegistrations"
        @click="handleClickOutside"
      />

      <div class="billing-line-content__block__row">
        <div class="billing-line-content__block__row--large">
          <SkOroraInput
            ref="nameInput"
            v-model.trim="name"
            type="text"
            class="input-row--large"
            :label="nameInputLabel"
            :errored="!!nameErrorMessage"
            :error-message="nameErrorMessage"
            :debounce="handleNameChanges"
            :debounce-ms="300"
          />
          <div>
            <SkDropdown
              ref="companyNameDropdown"
              placement="bottom-start"
              y-offset="10px"
              :trigger="null"
            >
              <!-- eslint disabled because we need to have 2 menus in the same page -->
              <!-- but html ids are unique -->
              <!-- eslint-disable-next-line vue/v-slot-style -->
              <template v-slot:menu>
                <SuggestedCompanyRegistrations
                  v-if="showCompanyNameDropdown"
                  :suggested-companies="suggestedCompanyRegistrations"
                  :search-input="name"
                  :related-input="$refs.nameInput"
                  @select-suggested-company="autofillBillingInfos"
                />
              </template>
            </SkDropdown>
          </div>
        </div>

        <div class="billing-line-content__block__row--small">
          <SkOroraInput
            ref="registrationNumberInput"
            v-model.trim="registrationNumber"
            class="input-row--large"
            type="text"
            :label="registrationNumberInputLabel"
            :errored="!!registrationNumberErrorMessage"
            :error-message="registrationNumberErrorMessage"
            :debounce="handleAndValidateRegistrationNumber"
            :debounce-ms="300"
          />
          <SkDropdown
            ref="registrationNumberDropdown"
            placement="bottom-start"
            y-offset="10px"
            :trigger="null"
          >
            <!-- eslint disabled because we need to have 2 menus in the same page -->
            <!-- but html ids are unique -->
            <!-- eslint-disable-next-line vue/v-slot-style -->
            <template v-slot:menu>
              <SuggestedCompanyRegistrations
                v-if="showRegistrationNumberDropdown"
                :suggested-companies="suggestedCompanyRegistrations"
                :search-input="registrationNumber"
                :related-input="$refs.registrationNumberInput"
                @select-suggested-company="autofillBillingInfos"
              />
            </template>
          </SkDropdown>
        </div>
      </div>

      <div class="billing-line-content__block__row">
        <div class="billing-line-content__input--third">
          <SkOroraInput
            v-if="!displayPaymentByCreditCard && !displayPaymentByBankTransfer"
            ref="iban"
            v-model.trim="iban"
            class="billing-line-content__input--third"
            type="text"
            :label="$t('fill_billing_info.billing_line.iban_input')"
            :errored="!!ibanErrorMessage"
            :error-message="ibanErrorMessage"
            :debounce="validateIban"
            :debounce-ms="100"
          />
        </div>
        <div
          :class="showSdiField ?
            'billing-line-content__input--third' :
            'billing-line-content__block__row--large'"
        >
          <SkOroraInput
            v-model.trim="email"
            class="input-row--large"
            type="text"
            :label="$t('fill_billing_info.billing_line.email_input')"
            :errored="!!emailErrorMessage"
            :error-message="emailErrorMessage"
            :debounce="validateEmail"
            :debounce-ms="100"
          />
        </div>
        <div
          v-if="showSdiField"
          class="billing-line-content__input--third"
        >
          <SkOroraInput
            ref="sdiInput"
            v-model.trim="sdi"
            data-test="sdi-input"
            class="input-row--large"
            type="text"
            :label="$t('fill_billing_info.billing_line.sdi_input')"
            :placeholder="$t('fill_billing_info.billing_line.sdi_placeholder')"
            :errored="!!sdiErrorMessage"
            :error-message="sdiErrorMessage"
          />
        </div>
      </div>
    </div>
    <div class="billing-line-content__block">
      <span class="sk-text-large-semibold">
        {{ $t('fill_billing_info.billing_line.billing_address_label') }}
      </span>

      <SkOroraInput
        v-model.trim="address"
        class="billing-line-content__input--full"
        type="text"
        :label="$t('fill_billing_info.billing_line.address_input')"
        :errored="!!addressErrorMessage"
        :error-message="addressErrorMessage"
        :debounce="validatePresence('address')"
        :debounce-ms="100"
      />

      <div class="billing-line-content__block__row--spaced">
        <SkOroraInput
          v-model.trim="zipcode"
          class="billing-line-content__input--third"
          type="text"
          :label="$t('fill_billing_info.billing_line.zipcode_input')"
          :errored="!!zipcodeErrorMessage"
          :error-message="zipcodeErrorMessage"
          :debounce="validatePresence('zipcode')"
          :debounce-ms="100"
        />
        <SkOroraInput
          v-model.trim="city"
          class="billing-line-content__input--third"
          type="text"
          :label="$t('fill_billing_info.billing_line.city_input')"
          :errored="!!cityErrorMessage"
          :error-message="cityErrorMessage"
          :debounce="validatePresence('city')"
          :debounce-ms="100"
        />
        <SkOroraSelect
          v-model="paymentInfos.billingAddress.country"
          class="billing-line-content__input--third"
          type="text"
          :label="$t('fill_billing_info.billing_line.country_input')"
          :options="countryOption"
          disabled
        />
      </div>
    </div>

    <div
      v-if="displayPaymentByBankTransfer"
      class="billing-line-content__block billing-line-content__block--column"
    >
      <span class="sk-text-large-semibold">
        {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method') }}
      </span>

      <div class="billing-line-content__payment-badge">
        <span class="sk-text-medium-regular">
          {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method_label') }}
          <span class="sk-text-medium-semibold">
            {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method_span') }}
          </span>
        </span>
      </div>

      <span class="sk-text-medium-regular">
        {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method_description') }}
      </span>
    </div>
    <CreditCardSection
      v-if="displayPaymentByCreditCard"
      v-model="paymentInfos"
      @credit-card-added="handleNewCreditCard"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  SkDropdown,
  SkOroraSelect,
  SkOroraInput,
} from '@skelloapp/skello-ui';
import GlobalEvents from 'vue-global-events';
import { billingAutomationClient } from '@/shared/utils/clients/billingAutomationClient';
import {
  isValidEmail,
  isValidIban,
  isValidSiret,
  isValidSiren,
} from '@/shared/utils/validators';
import { removeWhiteSpaces } from '@/shared/utils/method_helper';
import SuggestedCompanyRegistrations from './SuggestedCompanyRegistrations/index.vue';
import CreditCardSection from './CreditCardSection/index.vue';
import { getNaf } from '@/shared/utils/naf';

export default {
  name: 'BillingLineContent',
  components: {
    GlobalEvents,
    SkDropdown,
    SkOroraSelect,
    SkOroraInput,
    CreditCardSection,
    SuggestedCompanyRegistrations,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    showSdiField: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countryOption: null,
      registrationNumberWithSpacesAllowed: null,
      ibanWithSpacesAllowed: null,
      nameErrorMessage: null,
      registrationNumberErrorMessage: null,
      ibanErrorMessage: null,
      emailErrorMessage: null,
      addressErrorMessage: null,
      cityErrorMessage: null,
      zipcodeErrorMessage: null,
      sdiDirty: false,
      suggestedCompanyRegistrations: [],
      isDropDownOpen: {
        companyName: false,
        registrationNumber: false,
      },
    };
  },
  computed: {
    ...mapGetters(['displayPaymentByCreditCard', 'isBillingOnOrganisation', 'displayPaymentByBankTransfer']),
    paymentInfos: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', { ...newValue });
      },
    },
    areGenericInputsInvalid() {
      const sdiInvalid = this.showSdiField && (!!this.sdiErrorMessage || !this.sdi);
      return !!this.nameErrorMessage || !!this.registrationNumberErrorMessage ||
        !!this.emailErrorMessage || !!this.addressErrorMessage || !!this.cityErrorMessage ||
        !!this.zipcodeErrorMessage || !this.name || !this.registrationNumber ||
        !this.email || !this.address || !this.city || !this.zipcode ||
        sdiInvalid;
    },
    areInputsInvalid() {
      if (this.displayPaymentByCreditCard) {
        return this.areGenericInputsInvalid || !this.paymentInfos.hasValidPaymentMethod;
      }
      if (this.displayPaymentByBankTransfer) {
        return this.areGenericInputsInvalid;
      }
      return this.areGenericInputsInvalid || !!this.ibanErrorMessage || !this.iban;
    },
    address: {
      get() {
        return this.paymentInfos.billingAddress.line1;
      },
      set(value) {
        this.updatePaymentInfos('billingAddress', {
          ...this.paymentInfos.billingAddress,
          line1: value,
        });
      },
    },
    city: {
      get() {
        return this.paymentInfos.billingAddress.city;
      },
      set(value) {
        this.updatePaymentInfos('billingAddress', {
          ...this.paymentInfos.billingAddress,
          city: value,
        });
      },
    },
    email: {
      get() {
        return this.paymentInfos.billingAddress.email;
      },
      set(value) {
        this.updatePaymentInfos('billingAddress', {
          ...this.paymentInfos.billingAddress,
          email: value,
        });
      },
    },
    iban: {
      get() {
        return this.ibanWithSpacesAllowed &&
          removeWhiteSpaces(this.ibanWithSpacesAllowed);
      },
      set(value) {
        this.ibanWithSpacesAllowed = value;
        this.updatePaymentInfos('iban', this.iban);
      },
    },
    name: {
      get() {
        return this.paymentInfos.name;
      },
      set(value) {
        this.validatePresence('name');
        this.updatePaymentInfos('name', value);
      },
    },
    registrationNumber: {
      get() {
        return this.registrationNumberWithSpacesAllowed &&
          removeWhiteSpaces(this.registrationNumberWithSpacesAllowed);
      },
      set(value) {
        this.validateRegistrationNumber();
        this.registrationNumberWithSpacesAllowed = value;
        this.updatePaymentInfos('registrationNumber', this.registrationNumber);
      },
    },
    zipcode: {
      get() {
        return this.paymentInfos.billingAddress.zip;
      },
      set(value) {
        this.updatePaymentInfos('billingAddress', {
          ...this.paymentInfos.billingAddress,
          zip: value,
        });
      },
    },
    sdi: {
      get() {
        return this.paymentInfos.sdi;
      },
      set(value) {
        this.sdiDirty = true;
        this.updatePaymentInfos('sdi', value);
      },
    },
    nameInputLabel() {
      const i18nKey = this.isBillingOnOrganisation ?
        'organisation_name_input' :
        'shop_name_input';

      return this.$t(`fill_billing_info.billing_line.${i18nKey}`);
    },

    // Only INSEE API is implemented to look for company registrations for the moment.
    isSupportedCountryForRegistrationLookup() {
      return this.paymentInfos.billingAddress.country_code === 'FR';
    },
    registrationNumberInputLabel() {
      const i18nKey = this.isBillingOnOrganisation ?
        'organisation_registration_number_input' :
        'shop_registration_number_input';

      return this.$t(`fill_billing_info.billing_line.${i18nKey}`);
    },
    showCompanyNameDropdown() {
      return this.suggestedCompanyRegistrations.length > 0 &&
        this.isDropDownOpen.companyName;
    },
    showRegistrationNumberDropdown() {
      return this.suggestedCompanyRegistrations.length > 0 &&
        this.isDropDownOpen.registrationNumber;
    },
    sdiErrorMessage() {
      if (!this.showSdiField) return null;
      if (!this.sdiDirty) return null;
      if (!this.sdi) return this.$t('fill_billing_info.billing_line.error_input_mandatory');
      if (!/^[a-zA-Z0-9]{6,7}$/.test(this.sdi)) return this.$t('fill_billing_info.billing_line.sdi_invalid');

      return null;
    },
  },
  created() {
    this.countryOption = [{
      id: this.paymentInfos.billingAddress?.country,
      text: this.$t(`common.countries.${this.paymentInfos.billingAddress?.country_code}`),
    }];
    this.registrationNumber = this.paymentInfos?.registrationNumber;
    this.iban = this.paymentInfos?.iban;

    if (this.iban) this.validateIban();
    if (this.email) this.validateEmail();
    if (this.registrationNumber) this.validateRegistrationNumber();
  },
  updated() {
    if (this.areInputsInvalid) {
      this.$emit(
        this.isBillingOnOrganisation ? 'disable-submit' : 'disable-line-submit',
      );
      return;
    }

    this.$emit(
      this.isBillingOnOrganisation ? 'enable-submit' : 'enable-line-submit',
    );
  },
  methods: {
    lookupCompanyRegistrationsByName() {
      return billingAutomationClient
        .getCompanyRegistrationsByName(this.name);
    },
    lookupCompanyRegistrationsByRegistrationNumber() {
      if (this.isBillingOnOrganisation) {
        return billingAutomationClient
          .getCompanyRegistrationsByOrganisationRegistrationNumber(this.registrationNumber);
      }
      return billingAutomationClient
        .getCompanyRegistrationsByShopRegistrationNumber(this.registrationNumber);
    },
    populateSuggestedCompanyRegistrations(suggestedCompanies, searchedText) {
      this.suggestedCompanyRegistrations = suggestedCompanies.map(suggestedCompany => ({
        address: suggestedCompany.address,
        city: suggestedCompany.city,
        creationDate: suggestedCompany.creationDate,
        shopRegistrationNumber: suggestedCompany.shopRegistrationNumber,
        displayLabel: this.highlightMatch(
          suggestedCompany.name,
          searchedText,
        ),
        // We need to remove the dot in the main activity to be able to use the naf filter
        // Dots are added by the INSEE API
        mainActivity: getNaf(suggestedCompany.mainActivity.replaceAll('.', '')),
        name: suggestedCompany.name,
        organisationRegistrationNumber: suggestedCompany.organisationRegistrationNumber,
        zipCode: suggestedCompany.zipCode,
      }));
    },
    handleNameChanges() {
      this.validatePresence('name');
      if (!this.isSupportedCountryForRegistrationLookup) return;
      if (this.name.length < 3) {
        this.clearSuggestedCompanyRegistrations();
        return;
      }

      this.lookupCompanyRegistrationsByName()
        .then(response => {
          this.populateSuggestedCompanyRegistrations(response, this.name);
          this.isDropDownOpen.companyName = true;
          this.isDropDownOpen.registrationNumber = false;
          this.$refs.companyNameDropdown.open();
          this.$refs.registrationNumberDropdown.hide();
        }).catch(this.clearSuggestedCompanyRegistrations);
    },
    validatePresence(attribute) {
      const value = this[attribute];

      if (value === '') {
        this[`${attribute}ErrorMessage`] = this.$t('fill_billing_info.billing_line.error_input_mandatory');
      } else {
        this[`${attribute}ErrorMessage`] = null;
      }
    },
    validateIban() {
      this.validatePresence('iban');
      if (this.iban && !isValidIban(this.iban)) {
        this.ibanErrorMessage = this.$t('fill_billing_info.billing_line.iban_invalid');
      }
    },
    validateEmail() {
      this.validatePresence('email');
      if (this.email && !isValidEmail(this.email)) {
        this.emailErrorMessage = this.$t('fill_billing_info.billing_line.email_invalid');
      }
    },
    handleAndValidateRegistrationNumber() {
      this.handleRegistrationNumberInput();
      this.validateRegistrationNumber();
    },
    handleRegistrationNumberInput() {
      if (!this.isSupportedCountryForRegistrationLookup) return;
      if (this.registrationNumber.length < 3) {
        this.clearSuggestedCompanyRegistrations();
        return;
      }

      this.lookupCompanyRegistrationsByRegistrationNumber()
        .then(response => {
          this.populateSuggestedCompanyRegistrations(response, '');
          this.isDropDownOpen.registrationNumber = true;
          this.isDropDownOpen.companyName = false;
          this.$refs.registrationNumberDropdown.open();
          this.$refs.companyNameDropdown.hide();
        }).catch(this.clearSuggestedCompanyRegistrations);
    },
    validateRegistrationNumber() {
      this.validatePresence('registrationNumber');
      if (!this.registrationNumber || !this.isSupportedCountryForRegistrationLookup) return;

      if (this.isBillingOnOrganisation && !isValidSiren(this.registrationNumber)) {
        this.registrationNumberErrorMessage = this.$t('fill_billing_info.billing_line.siren_invalid');
      } else if (!this.isBillingOnOrganisation && !isValidSiret(this.registrationNumber)) {
        this.registrationNumberErrorMessage = this.$t('fill_billing_info.billing_line.siret_invalid');
      }
    },

    // for the companyName "L'AMI DU PAIN" with 'AMI D' as input, we return the array:
    // [{ text: "L'" },
    //  { text: 'AMI D', isHighlighted: true },
    //  { text: 'U PAIN' }]
    //
    highlightMatch(result, searchedText) {
      const matching = result.toLowerCase().match(searchedText.toLowerCase());
      if (!matching) return [{ text: result }];

      const formattedText = [
        { text: searchedText, isHighlighted: true },
        { text: result.substring(matching.index + searchedText.length) },
      ];

      if (matching.index > 0) {
        formattedText.unshift({ text: result.substring(0, matching.index) });
      }

      return formattedText;
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown?.$el?.contains(event.target)) return;

      this.clearSuggestedCompanyRegistrations();
    },
    clearSuggestedCompanyRegistrations() {
      this.suggestedCompanyRegistrations = [];
      this.isDropDownOpen.registrationNumber = false;
      this.isDropDownOpen.companyName = false;
      this.$refs.companyNameDropdown.hide();
      this.$refs.registrationNumberDropdown.hide();
    },
    autofillBillingInfos(company) {
      this.name = company.name;
      this.registrationNumber = this.isBillingOnOrganisation ? // SIRET for shop, SIREN for organisation
        company.organisationRegistrationNumber : company.shopRegistrationNumber;
      this.address = company.address;
      this.city = company.city;
      this.zipcode = company.zipCode;
      this.clearSuggestedCompanyRegistrations();
    },
    handleNewCreditCard() {
      if (this.areInputsInvalid) return;

      this.$emit(
        this.isBillingOnOrganisation ? 'enable-submit' : 'enable-line-submit',
      );
    },
    updatePaymentInfos(attribute, value) {
      this.paymentInfos = {
        ...this.paymentInfos,
        [attribute]: value,
      };
    },
  },
};
</script>

<style lang="scss" scoped>

.billing-line-content__container {
  display: flex;
  padding: 24px;
  flex-direction: column;
  gap: 40px;
  align-self: stretch;
}

.billing-line-content__block {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.billing-line-content__block--column {
  flex-direction: column;
  flex-wrap: nowrap;
}

.billing-line-content__payment-badge {
  border-radius: 4px;
  border: 1px solid $sk-grey-10;
  padding: 6px;
  width: fit-content;
  line-height: normal;
}

.billing-line-content__block__row {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 16px;
}

.billing-line-content__block__row--large {
  width: calc(66% + 16px); // 16px is the gap between the two inputs
}

.billing-line-content__block__row--small {
  width: 33%;
}

.billing-line-content__block__row--spaced {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 16px;
  justify-content: space-between;
}

.billing-line-content__input--full {
  width: 100%;
}

.billing-line-content__input--half {
  width: 45%;
}

.billing-line-content__input--third {
  width: 33%;
}

.billing-line__label {
  margin: 24px 0 16px;
  font-size: $fs-text-l;
  font-weight: $fw-semi-bold;
  line-height: 17px;
}

.billing-line__label:first-of-type {
  margin: 14px 0 16px;
}

.input-row--large {
  max-width: 100%;
}
</style>
